import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import Badge from '../components/ui/Badge';
import { getAdminDrivers } from '../store/thunks/driverActions';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@mui/material';

/**
 * Redesigned driver management screen backed by /api/v1/admin/drivers.
 * Provides quick-filter chips for the operational states we care about
 * (active, inactive, restricted, missing docs, below 3.0) and server-side
 * search + market filtering so the list scales past client-filterable sizes.
 */

const FILTER_CHIPS = [
  { id: 'all', label: 'All Drivers' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'restricted', label: 'Restricted' },
  { id: 'missing_docs', label: 'Missing Docs' },
  { id: 'below_rating', label: 'Below 3.0' },
];

const complianceVariant = {
  ready: 'success',
  pending: 'warning',
  rejected: 'danger',
  missing: 'default',
};

const complianceLabel = {
  ready: 'Ready',
  pending: 'Pending Review',
  rejected: 'Rejected',
  missing: 'Missing',
};

const CHECKR_CLEARED_STATUSES = ['clear', 'cleared'];

function RatingStar({ value }) {
  const rounded = Number(value ?? 5).toFixed(1);
  const tone = value < 3 ? 'text-red-600' : value < 4 ? 'text-amber-600' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M10 15.27 16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
      </svg>
      {rounded}
    </span>
  );
}

function DriverListScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [market, setMarket] = useState('');

  const { adminDriversLoading, adminDrivers, adminDriversError } = useSelector(
    (state) => state.allDrivers ?? {}
  );

  useEffect(() => {
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (search.trim()) params.search = search.trim();
    if (market.trim()) params.market = market.trim();
    const timer = setTimeout(() => {
      dispatch(getAdminDrivers(params));
    }, 200);
    return () => clearTimeout(timer);
  }, [dispatch, filter, search, market]);

  const drivers = useMemo(() => adminDrivers?.drivers ?? [], [adminDrivers]);

  return (
    <div className="flex w-full h-screen overflow-auto">
      {adminDriversError && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', width: '90%', margin: 'auto', top: 20, left: 0, right: 0, zIndex: 10 }}
        >
          <strong>{adminDriversError?.message || 'Could not load drivers.'}</strong>
        </Alert>
      )}

      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader heading={'Drivers'} pages={'Pages / Drivers'} />
        </div>

        <div className="px-6 py-5 h-screen overflow-auto">
          {/* Filters + search */}
          <div className="bg-white rounded-xl px-5 py-4 mb-4 shadow-sm">
            <div className="flex flex-wrap gap-2 mb-3">
              {FILTER_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  onClick={() => setFilter(chip.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                    filter === chip.id
                      ? 'bg-[#4F46E5] text-white border-[#4F46E5]'
                      : 'bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#A5B4FC]'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <input
                className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Search by name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <input
                className="w-48 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Filter by market"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
              />
            </div>
          </div>

          {/* Drivers table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#71717A] text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Driver</th>
                    <th className="text-left px-5 py-3 font-semibold">Contact</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                    <th className="text-left px-5 py-3 font-semibold">Rating</th>
                    <th className="text-left px-5 py-3 font-semibold">Compliance</th>
                    <th className="text-left px-5 py-3 font-semibold">Checkr</th>
                    <th className="text-left px-5 py-3 font-semibold">Market</th>
                    <th className="text-left px-5 py-3 font-semibold">Orders</th>
                    <th className="text-left px-5 py-3 font-semibold">Last Active</th>
                    <th className="text-left px-5 py-3 font-semibold">Sign Up</th>
                    <th className="text-right px-5 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminDriversLoading && (
                    <tr>
                      <td colSpan={11} className="text-center py-10 text-[#71717A]">
                        Loading drivers…
                      </td>
                    </tr>
                  )}
                  {!adminDriversLoading && drivers.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-10 text-[#71717A]">
                        No drivers match the current filters.
                      </td>
                    </tr>
                  )}
                  {drivers.map((d) => {
                    // Prefer the server-computed effectiveStatus — it
                    // captures "can this driver actually work right now?"
                    // (isActive + not suspended + docs approved + Checkr
                    // cleared). Falls back to the legacy boolean for older
                    // deploys that haven't shipped the new field yet.
                    const isSuspended =
                      d.suspendedUntil && new Date(d.suspendedUntil) > new Date();
                    const checkrCleared = CHECKR_CLEARED_STATUSES.includes(d.checkrVerified);
                    const effective =
                      d.effectiveStatus ||
                      (isSuspended
                        ? 'suspended'
                        : d.isActive && checkrCleared
                          ? 'active'
                          : 'inactive');
                    const STATUS_META = {
                      active: { label: 'Active', variant: 'success' },
                      inactive: { label: 'Inactive', variant: 'default' },
                      suspended: { label: 'Suspended', variant: 'warning' },
                      pending_docs: { label: 'Pending Docs', variant: 'warning' },
                    };
                    const meta = STATUS_META[effective] || STATUS_META.inactive;

                    const checkr = d.checkrVerified || 'pending';
                    const CHECKR_META = {
                      clear: { label: 'Cleared', variant: 'success' },
                      cleared: { label: 'Cleared', variant: 'success' },
                      pending: { label: 'Pending', variant: 'default' },
                      consider: { label: 'Consider', variant: 'warning' },
                      cancelled: { label: 'Cancelled', variant: 'danger' },
                      created: { label: 'Created', variant: 'default' },
                    };
                    const checkrMeta = CHECKR_META[checkr] || CHECKR_META.pending;

                    const driverId = d._id || d.id;
                    const openDriver = () => {
                      if (driverId) navigate(`/driverList/${driverId}`);
                    };

                    return (
                      <tr
                        key={driverId || d.email}
                        onClick={openDriver}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDriver();
                          }
                        }}
                        tabIndex={driverId ? 0 : -1}
                        role="button"
                        className={`
                          border-t border-[#F4F4F5] transition-colors
                          ${driverId ? 'cursor-pointer hover:bg-[#FAFAFA] focus:bg-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#4F46E5]/30' : ''}
                        `}
                        title={driverId ? `Open ${d.firstName || ''} ${d.lastName || ''}`.trim() : undefined}
                      >
                        {/* Driver */}
                        <td className="px-5 py-4">
                          <div className="font-medium text-[#18181B] hover:text-[#4F46E5]">
                            {d.firstName} {d.lastName}
                          </div>
                          <div className="text-[10px] font-mono text-[#A1A1AA] mt-0.5">
                            {String(d._id || '').slice(-6)}
                          </div>
                        </td>

                        {/* Contact — clickable mailto / tel so ops can
                            reach the driver in one click. */}
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-0.5">
                            {d.email ? (
                              <a
                                href={`mailto:${d.email}`}
                                title={d.email}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-[#4F46E5] hover:underline truncate max-w-[220px]"
                              >
                                {d.email}
                              </a>
                            ) : (
                              <span className="text-xs text-[#A1A1AA]">no email</span>
                            )}
                            {d.phoneNumber ? (
                              <a
                                href={`tel:${d.phoneNumber}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-[#3F3F46] hover:text-[#4F46E5] hover:underline"
                              >
                                {d.phoneNumber}
                              </a>
                            ) : (
                              <span className="text-xs text-[#A1A1AA]">no phone</span>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4">
                          <Badge variant={meta.variant} dot>
                            {meta.label}
                          </Badge>
                          {d.eligibility && !d.eligibility.eligible && (
                            <div className="mt-1 text-[10px] text-[#B91C1C] uppercase tracking-wide">
                              {d.eligibility.reasons[0]?.replaceAll('_', ' ')}
                            </div>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <RatingStar value={d.rating} />
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant={complianceVariant[d.compliance?.state] || 'default'}>
                            {complianceLabel[d.compliance?.state] || 'Missing'}
                          </Badge>
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant={checkrMeta.variant}>{checkrMeta.label}</Badge>
                        </td>
                        <td className="px-5 py-4 text-[#3F3F46]">{d.market || '—'}</td>
                        <td className="px-5 py-4 text-[#3F3F46]">{d.totalOrders || 0}</td>
                        <td className="px-5 py-4 text-[#71717A] text-xs">
                          {d.lastOrderAt ? new Date(d.lastOrderAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-4 text-[#71717A] text-xs whitespace-nowrap">
                          {d.createdAt
                            ? new Date(d.createdAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            className="bg-[#4F46E5] text-white text-xs px-4 py-1.5 rounded-full hover:bg-[#4338CA]"
                            disabled={!driverId}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDriver();
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverListScreen;
